import styled from '@emotion/styled'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Tooltip from '@mui/material/Tooltip'
import { isEmpty, keys } from 'ramda'
import React from 'react'

import { Select } from '@/app/component/atom/form'
import CreateCustomFieldsDialog from '@/app/module/custom-fields/component/create-custom-fields-dialog'
import { FormSpecs } from '@/app/types'

import { UploadConfigs } from '../../types'

type Props = {
  configs: UploadConfigs
  errors: string[]
  specs: FormSpecs
  statuses: boolean[]
  onConfigChange: (payload: { errors: string[]; configs: UploadConfigs }) => void
  onStatusChange: (payload: { errors: string[]; statuses: boolean[] }) => void
}

class ContactsUploadConfigureContent extends React.Component<Props, { isDialogOpen: boolean }> {
  state = { isDialogOpen: false }

  closeHandler = () => this.setState({ isDialogOpen: false })

  render() {
    const { configs, errors, specs, statuses, onConfigChange, onStatusChange } = this.props

    const excludedSystemFields = ['createdAt', 'updatedAt', 'lastUseAt', 'carrier']
    const values = Object.keys(specs)
      .filter((fieldId) => !excludedSystemFields.includes(fieldId))
      .map((fieldId) => ({
        label: specs[fieldId].label || '',
        value: fieldId.toString(),
      }))

    return (
      <div>
        <CreateCustomFieldsDialog open={this.state.isDialogOpen} onClose={this.closeHandler} />
        <TopWrap>
          <Button color="primary" variant="outlined" onClick={() => this.setState({ isDialogOpen: true })}>
            Add new fields
          </Button>
        </TopWrap>
        {!isEmpty(configs) &&
          configs.titlesFromFile.map((name, colIndex) => (
            <MainWrap key={name}>
              <MainLeft>
                <Tooltip title={getCheckTooltipTitle(statuses[colIndex])} arrow>
                  <Checkbox
                    color="primary"
                    checked={statuses[colIndex]}
                    onChange={(e) => {
                      onStatusChange({
                        errors: errors.map((err, i) => (colIndex === i ? '' : err)),
                        statuses: statuses.map((currentValue, i) => (colIndex === i ? e.target.checked : currentValue)),
                      })
                    }}
                  />
                </Tooltip>
              </MainLeft>
              <MainRight>
                <Select
                  name={name}
                  label={name}
                  error={errors[colIndex]}
                  values={[{ label: 'None', value: '', disabled: true }, ...values]}
                  value={getValue(configs, colIndex.toString())}
                  editable={statuses[colIndex]}
                  info={getInfo(configs, colIndex.toString())}
                  onChange={({ value }: { value: string }) => {
                    const type = value.indexOf('customFields.') === 0 ? 'customFieldsToColumns' : 'fieldsToColumns'
                    const fieldName = value.replace('customFields.', '')
                    const valueStr = colIndex.toString()
                    const resetConfigs = resetWhereMatch(configs, valueStr)
                    const merged: UploadConfigs = {
                      ...resetConfigs,
                      [type]: {
                        ...resetConfigs[type],
                        [fieldName]: valueStr,
                      },
                    }
                    onConfigChange({
                      errors: errors.map((err, i) => (i === colIndex ? '' : err)),
                      configs: merged,
                    })
                  }}
                />
              </MainRight>
            </MainWrap>
          ))}
      </div>
    )
  }
}

const resetWhereMatch = (configs: UploadConfigs, value: string): UploadConfigs => ({
  ...configs,
  fieldsToColumns: Object.keys(configs.fieldsToColumns).reduce<Record<string, string>>(
    (acc, fieldName) => ({
      ...acc,
      [fieldName]: configs.fieldsToColumns[fieldName] === value ? '' : configs.fieldsToColumns[fieldName],
    }),
    {},
  ),
  customFieldsToColumns: Object.keys(configs.customFieldsToColumns).reduce<Record<string, string>>(
    (acc, fieldName) => ({
      ...acc,
      [fieldName]: configs.customFieldsToColumns[fieldName] === value ? '' : configs.customFieldsToColumns[fieldName],
    }),
    {},
  ),
})

const getValue = (config: UploadConfigs, index: string): string => {
  const matchedField = keys(config.fieldsToColumns).find((field) => config.fieldsToColumns[field] === index)
  const matchedCustomField = keys(config.customFieldsToColumns).find(
    (field) => config.customFieldsToColumns[field] === index,
  )
  return matchedField || (matchedCustomField && `customFields.${matchedCustomField}`) || ''
}

const getInfo = (config: UploadConfigs, colIndex: string): string => {
  const idx = parseInt(colIndex, 10)
  const sampleDisplay = config.sampleData.filter((samples) => samples[idx] !== '').map((samples) => `"${samples[idx]}"`)
  if (!sampleDisplay.length) {
    return 'no samples available'
  }
  return `samples: ${sampleDisplay.join(', ')}`
}

const getCheckTooltipTitle = (isChecked: boolean) => (isChecked ? 'Disable column' : 'Enable column')

const TopWrap = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  margin: '1rem 0',
})

const MainWrap = styled('div')({
  display: 'flex',
  alignItems: 'center',
})

const MainRight = styled('div')({
  flex: '1 1 auto',
})

const MainLeft = styled('div')({
  flex: '0 0 auto',
})

export default ContactsUploadConfigureContent
